import EventTarget from '@ungap/event-target'; // polyfill that cannot be resolved globally
import { TimedGroup, calculateGroupTimes } from './TimedGroup';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useCapabilities } from '../../services/hooks/useCapabilities';
import moment, { Moment } from 'moment-timezone';
import { MOM_SERVER } from './MapConfig';

const momentToIsoNoMillis = (val: Moment) =>
  val.toISOString().split('.')[0] + 'Z';

// handles all the logic to for a set of timed layer groups
export const useTimedLayerGroups = (layerGroups: TimedGroup[]) => {
  const [refresher] = useState(() => new EventTarget());
  const [isPlaying, setIsPlaying] = useState(false);
  const [layerGroup, innerSetLayerGroup] = useState(layerGroups[0]);
  const [timeIndex, setTimeIndex] = useState<number | null>(null);

  const timedLayers = useMemo(
    () =>
      layerGroups.reduce(
        (acc, x) => acc.concat(x.layers.map((l) => l.key)),
        [] as string[]
      ),
    [layerGroups]
  );
  const capabilities = useCapabilities(timedLayers, MOM_SERVER.capabilityId!);
  const refreshCapabilities = useCallback(
    () => refresher.dispatchEvent(new Event('refresh')),
    [refresher]
  );
  // calculate which time to use based on picasso layers and the merge strategy
  const momentTimes = useMemo(() => {
    return calculateGroupTimes(layerGroup, capabilities);
  }, [capabilities, layerGroup]);
  const times = useMemo(() => momentTimes?.map(momentToIsoNoMillis), [
    momentTimes,
  ]);

  // sets the time index to the time index closest to the supplied time
  const setToClosestTime = useCallback((time: Moment, times: Moment[]) => {
    if (!times.length) return;
    let closestTime = 0;
    let diff = times[0].diff(time);
    for (let i = 1; i < times.length; ++i) {
      const diff2 = times[i].diff(time);
      if (Math.abs(diff2) < Math.abs(diff)) {
        closestTime = i;
        diff = diff2;
      }
    }
    setTimeIndex(closestTime);
  }, []);

  // set the layer group and the time index to the closest corresponding time
  const setLayerGroup = useCallback(
    (layerGroup: TimedGroup) => {
      innerSetLayerGroup(layerGroup);
      const newTimes = calculateGroupTimes(layerGroup, capabilities);
      if (timeIndex === null) return;
      if (!momentTimes?.length || !newTimes?.length) setTimeIndex(null);
      else setToClosestTime(momentTimes[timeIndex], newTimes);
    },
    [momentTimes, capabilities, setToClosestTime, timeIndex]
  );

  // sets the initial time index to the closest available time
  useEffect(() => {
    if (timeIndex !== null) return;
    if (!momentTimes?.length) return;
    setToClosestTime(moment(), momentTimes);
  }, [timeIndex === null && momentTimes]);

  const togglePlay = useCallback(() => {
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  // referrentially stable timeIndex
  const timeIndexRef = useRef(timeIndex);
  timeIndexRef.current = timeIndex;

  const nextTime = useCallback(() => {
    if (!times?.length) return;
    setTimeIndex(((timeIndexRef.current || 0) + 1) % times.length);
  }, [timeIndexRef, times?.length]);

  // referrentially stable isPlaying
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlayingRef.current) nextTime();
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlayingRef, nextTime]);

  return {
    layerGroup,
    refreshCapabilities,
    times,
    momentTimes,
    setLayerGroup,
    timeIndex,
    setTimeIndex,
    isPlaying,
    togglePlay,
    capabilities,
  };
};
